import cn from 'classnames';
import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'editor/src/store/hooks';
import changeQuantitySavingsQuantityOperation from 'editor/src/store/quantitySavings/operation/changeQuantitySavingsQuantityOperation';
import getQuantitySavingsQuote from 'editor/src/store/quantitySavings/selector/getQuantitySavingsQuote';
import useIsQuantitySavingsPending from 'editor/src/store/quantitySavings/useIsQuantitySavingsPending';
import getNextQuantitySavingsTier from 'editor/src/store/quantitySavings/util/getNextQuantitySavingsTier';
import getQuantitySavingsProgress from 'editor/src/store/quantitySavings/util/getQuantitySavingsProgress';

import useTr from 'editor/src/util/useTr';

import QuantityStepper from 'editor/src/component/DesktopSidebar/TabContents/GangSheetTabContent/QuantityStepper';

import styles from './index.module.scss';

/** Quantity and the host's price-break tiers, shown while the host sends `quantitySavings.setQuote`. */
function QuantitySavingsPanel() {
  const tr = useTr();
  const dispatch = useDispatch();
  const quote = useSelector(getQuantitySavingsQuote);
  const pendingQuantity = useSelector((state) => state.quantitySavings.pendingQuantity);
  const isPending = useIsQuantitySavingsPending();
  const onQuantityChange = useCallback((quantity: number) => {
    dispatch(changeQuantitySavingsQuantityOperation(quantity));
  }, []);

  if (!quote) {
    return null;
  }

  const next = getNextQuantitySavingsTier(quote);
  const activeTier = quote.tiers[quote.activeTierIndex];
  const progress = getQuantitySavingsProgress(quote);
  const quantityLabel = tr('editor-quantity-savings-quantity', 'Quantity');

  return (
    <div className={cn(styles.QuantitySavingsPanel, 'cy-quantity-savings')}>
      <div className={styles.quantityRow}>
        <span className={styles.quantityLabel}>{quantityLabel}</span>
        <QuantityStepper
          value={pendingQuantity ?? quote.quantity}
          min={quote.minQuantity ?? 1}
          max={quote.maxQuantity}
          step={quote.quantityStep}
          onChange={onQuantityChange}
        />
      </div>

      <div className={styles.card} aria-busy={isPending}>
        <div className={styles.cardHeader}>
          <span className={styles.title}>{tr('editor-quantity-savings-title', 'Bulk discounts')}</span>
          <span className={styles.subtitle}>
            {tr('editor-quantity-savings-subtitle', 'The more you order, the more you save')}
          </span>
        </div>

        <div className={styles.progress}>
          <div className={styles.progressLabels}>
            <span className="cy-quantity-savings-unlocked">
              {activeTier?.savingText
                ? tr('editor-quantity-savings-unlocked', '{{saving}} off unlocked', { saving: activeTier.savingText })
                : tr('editor-quantity-savings-no-discount', 'No discount yet')}
            </span>
            <span className="cy-quantity-savings-next">
              {!next && tr('editor-quantity-savings-best-price', 'Best price unlocked')}
              {next &&
                tr('editor-quantity-savings-next-saving', '{{count}} more for {{saving}} off', {
                  count: next.additionalQuantity,
                  saving: next.tier.savingText,
                })}
            </span>
          </div>
          <div className={styles.progressTrack} aria-hidden="true">
            <div className={styles.progressFill} style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        </div>

        <div role="table" aria-label={tr('editor-quantity-savings-tiers', 'Discount tiers')} className={styles.table}>
          <div role="row" className={cn(styles.row, styles.headRow)}>
            <span role="columnheader">{quantityLabel}</span>
            <span role="columnheader" className={styles.center}>
              {tr('editor-quantity-savings-per-item', 'Per item')}
            </span>
            <span role="columnheader" className={styles.end}>
              {tr('editor-quantity-savings-discount', 'Discount')}
            </span>
          </div>
          {quote.tiers.map((tier, index) => {
            const isActive = index === quote.activeTierIndex;
            return (
              <div
                role="row"
                key={tier.minimumQuantity}
                aria-current={isActive || undefined}
                className={cn(styles.row, 'cy-quantity-savings-tier', {
                  [styles.active]: isActive,
                })}
              >
                <span role="cell" className={styles.range}>
                  {tier.rangeText}
                  {isActive && <span className={styles.youBadge}>{tr('editor-quantity-savings-you', 'YOU')}</span>}
                </span>
                <span role="cell" className={styles.center}>
                  {tier.unitPriceText}
                </span>
                <span
                  role="cell"
                  className={cn(styles.end, {
                    [styles.saving]: !!tier.savingText,
                  })}
                >
                  {tier.savingText
                    ? tr('editor-quantity-savings-off', '{{saving}} off', { saving: tier.savingText })
                    : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default React.memo(QuantitySavingsPanel);
